import type { Branded } from '#layers/base/public';
import type { IndexKind, ReferentialAction } from '#layers/data-model/constants/schema/schema';
import type { DefaultValue } from './default-value';
import type {
	GlobalCollectionId,
	GlobalConstraintId,
	GlobalDataSourceId,
	GlobalIndexId,
	GlobalNamespaceId,
	GlobalPrimitiveFieldId,
	GlobalSingleRelationFieldId,
} from './ids';
import type { PrimitiveType } from './primitive-type';

/**
 * The wire format version this Studio build decodes. The engine stamps the
 * document's `formatVersion`; `decodeSchemaManifest` rejects any other value.
 */
export const SupportedSchemaManifestFormatVersion = 1;

/** Index into the document's interned `strings` pool. */
export type SchemaManifestStringRef = Branded<number, 'SchemaManifestStringRef'>;

/** Index into the document's interned `primitiveTypes` pool. */
export type SchemaManifestPrimitiveTypeRef = Branded<number, 'SchemaManifestPrimitiveTypeRef'>;

/** Index of an input object type in the query-schema graph's `inputObjectTypes`. */
export type SchemaManifestInputObjectTypeRef = Branded<number, 'SchemaManifestInputObjectTypeRef'>;

/** Index of a deduplicated field in the query-schema graph's `inputFields`. */
export type SchemaManifestInputFieldRef = Branded<number, 'SchemaManifestInputFieldRef'>;

/** Index of a deduplicated field in the query-schema graph's `outputFields`. */
export type SchemaManifestOutputFieldRef = Branded<number, 'SchemaManifestOutputFieldRef'>;

/**
 * A reference to a type in the query-schema graph: a pool letter plus the
 * index within that pool — `"i2"` (input object), `"o0"` (output object),
 * `"e1"` (enum), `"p3"` (primitive) — wrapped in the list/nullable grammar
 * (`"[i2]"`, `"p3?"`), nested as the schema nests them. The ref alone names
 * a type's kind; the pools are homogeneous. `p` indexes the document's
 * top-level `primitiveTypes`; the other letters index the graph's pools.
 */
export type SchemaManifestTypeRef = Branded<string, 'SchemaManifestTypeRef'>;

/** Position of a source in the document's `sources`. */
export type SchemaManifestSourceRef = Branded<number, 'SchemaManifestSourceRef'>;

/** Position of a namespace in the document's `namespaces`. */
export type SchemaManifestNamespaceRef = Branded<number, 'SchemaManifestNamespaceRef'>;

/** Position of a collection in the document's `collections`. */
export type SchemaManifestCollectionRef = Branded<number, 'SchemaManifestCollectionRef'>;

/** Position of a field within its collection's `primitiveFields`. */
export type SchemaManifestPrimitiveFieldRef = Branded<number, 'SchemaManifestPrimitiveFieldRef'>;

/** Position of a relation field within its collection's `relationFields`. */
export type SchemaManifestRelationRef = Branded<number, 'SchemaManifestRelationRef'>;

/**
 * The engine's schema manifest, served by `GET /{workspace}/schema`.
 *
 * The document is rendered from the engine's in-memory schema — no database
 * query behind it. Every name is a `SchemaManifestStringRef` into the shared
 * `strings` pool, and cross-references between entities are branded array
 * positions, not repeated ids. See `decodeSchemaManifest` for turning it back
 * into workspace-schema rows.
 */
export interface SchemaManifest {
	formatVersion: number;
	/** Interned strings; every `SchemaManifestStringRef` points into it. */
	strings: string[];
	/**
	 * Interned primitive types (`unsupported` included), shared by both
	 * sections: `SchemaManifestPrimitiveTypeRef` indexes and `p` type refs
	 * point into it.
	 */
	primitiveTypes: PrimitiveType[];
	monospaceSchema: MonospaceSchema;
	/** The serialized `QuerySchema` graph — what the /items API accepts. */
	querySchema: QuerySchema;
}

/** The engine's collection operation vocabulary, as it appears on the wire. */
export const QueryOperationKinds = {
	ReadMany: 'readMany',
	ReadOne: 'readOne',
	Create: 'create',
	UpdateMany: 'updateMany',
	UpdateOne: 'updateOne',
	DeleteMany: 'deleteMany',
	DeleteOne: 'deleteOne',
} as const;

export type OperationKind = (typeof QueryOperationKinds)[keyof typeof QueryOperationKinds];

/** What an input field maps to in the collection schema. */
export const SchemaManifestInputFieldKinds = {
	Primitive: 'primitive',
	Relation: 'relation',
} as const;

export type SchemaManifestInputFieldKind = (typeof SchemaManifestInputFieldKinds)[keyof typeof SchemaManifestInputFieldKinds];

/** The pool letter that opens a `SchemaManifestTypeRef`. */
export const SchemaManifestTypePools = {
	InputObject: 'i',
	OutputObject: 'o',
	Enum: 'e',
	Primitive: 'p',
} as const;

/**
 * The serialized `QuerySchema` graph: an operation list over flat pools.
 * Types and fields are deduplicated and referenced by index; a consumer
 * answers questions by following references, not by re-deriving rules.
 *
 * Every pool is homogeneous — a type reference names its pool in the ref
 * grammar, so a consumer never inspects a value to learn its kind.
 */
export interface QuerySchema {
	operations: Operation[];
	inputFields: InputField[];
	outputFields: OutputField[];
	inputObjectTypes: InputObjectType[];
	outputObjectTypes: OutputObjectType[];
	enumTypes: EnumType[];
}

/**
 * `[kind, collection name, arguments, output]`. Arguments are always an
 * input object, so a bare `inputObjectTypes` index does.
 */
export type Operation = [
	kind: OperationKind,
	collection: SchemaManifestStringRef,
	arguments: SchemaManifestInputObjectTypeRef,
	output: SchemaManifestTypeRef,
];

/**
 * A deduplicated field of an input object type — an `InputField` minus its
 * name, which lives in the owning object's `[name, field]` pair.
 */
export interface InputField {
	type: SchemaManifestTypeRef;
	optional?: boolean;
	/** Absent for syntax fields such as operators and combinators. */
	kind?: SchemaManifestInputFieldKind;
	/**
	 * The schema field this argument is an alias of, as its api name — the
	 * `key` argument of `readOne`/`updateOne`/`deleteOne` aliases the
	 * primary-key field. Absent when the field's own name is the schema field
	 * name (`kind` present) or the field is syntax.
	 */
	aliasOf?: SchemaManifestStringRef;
	/** Alternative shapes the REST API also accepts. */
	secondaryTypes?: SchemaManifestTypeRef[];
	hasDefault?: boolean;
}

/**
 * A deduplicated field of an output object type — an `OutputObjectField`
 * minus its name, which lives in the owning object's `[name, field]` pair.
 */
export interface OutputField {
	type: SchemaManifestTypeRef;
	/** Read-arguments input object of a relation field. */
	arguments?: SchemaManifestInputObjectTypeRef;
}

export interface InputObjectType {
	/**
	 * `[name, input field]` pairs flattened into one stride-2 array, in field
	 * order: even positions are name refs, odd positions input field refs.
	 * Flat because the nested pair arrays were the document's largest
	 * JSON.parse cost.
	 */
	fields: (SchemaManifestStringRef | SchemaManifestInputFieldRef)[];
	/** Names from `InputObjectTypeConstraints`. */
	constraints?: SchemaManifestStringRef[];
}

/** The engine's input-object constraints, as their names appear in `strings`. */
export const InputObjectTypeConstraints = {
	AtLeastOneField: 'atLeastOneField',
	AtMostOneField: 'atMostOneField',
} as const;

export interface OutputObjectType {
	/** `[name, output field]` pairs, flattened stride-2 like input objects. */
	fields: (SchemaManifestStringRef | SchemaManifestOutputFieldRef)[];
}

/** Enum variant names. */
export type EnumType = SchemaManifestStringRef[];

export interface MonospaceSchema {
	sources: MonospaceSource[];
	namespaces: MonospaceNamespace[];
	collections: MonospaceCollection[];
}

export interface MonospaceSource {
	id: GlobalDataSourceId;
	name: SchemaManifestStringRef;
	provider: SchemaManifestStringRef;
	schemaLocked?: boolean;
}

export interface MonospaceNamespace {
	id: GlobalNamespaceId;
	dbName: SchemaManifestStringRef;
	source: SchemaManifestSourceRef;
	isSystem?: boolean;
}

export interface MonospaceCollection {
	id: GlobalCollectionId;
	apiName: SchemaManifestStringRef;
	/** Omitted when equal to `apiName`, the common case. */
	dbName?: SchemaManifestStringRef;
	source: SchemaManifestSourceRef;
	namespace?: SchemaManifestNamespaceRef;
	isSystem?: boolean;
	primitiveFields: MonospacePrimitiveField[];
	/**
	 * Single relations only: union relation fields have no wire
	 * representation in this format version and are omitted entirely.
	 */
	relationFields: MonospaceRelationField[];
	indexes?: MonospaceIndex[];
}

export interface MonospacePrimitiveField {
	id: GlobalPrimitiveFieldId;
	apiName: SchemaManifestStringRef;
	/** Omitted when equal to `apiName`, the common case. */
	dbName?: SchemaManifestStringRef;
	/** The stored type, as an index into the document's `primitiveTypes`. */
	type: SchemaManifestPrimitiveTypeRef;
	isNullable?: boolean;
	isList?: boolean;
	isReadonly?: boolean;
	defaultValue?: DefaultValue;
}

export interface MonospaceRelationField {
	id: GlobalSingleRelationFieldId;
	apiName: SchemaManifestStringRef;
	oppositeCollection: SchemaManifestCollectionRef;
	/** The opposite relation field, within that collection's `relationFields`. */
	oppositeField: SchemaManifestRelationRef;
	/**
	 * The primitive fields backing the relation, as
	 * `[local field, opposite field]` pairs into the two collections'
	 * `primitiveFields`.
	 */
	linkingFields: [SchemaManifestPrimitiveFieldRef, SchemaManifestPrimitiveFieldRef][];
	/** Present on the side that owns the FK, which also carries the constraint. */
	isForward?: boolean;
	isList?: boolean;
	isNullable?: boolean;
	/** The FK constraint realizing the relation, on the forward side only. */
	constraint?: MonospaceConstraint;
}

export interface MonospaceConstraint {
	id: GlobalConstraintId;
	dbName: SchemaManifestStringRef;
	onDelete: ReferentialAction;
	onUpdate: ReferentialAction;
}

export interface MonospaceIndex {
	id: GlobalIndexId;
	dbName: SchemaManifestStringRef;
	kind: IndexKind;
	/** Member fields, as positions into the collection's `primitiveFields`. */
	fields: SchemaManifestPrimitiveFieldRef[];
}
